import { SagaIterator } from 'redux-saga';
import axios from 'axios';
import { isApiError, request } from '@wix/da-shared-react/pkg/xhr';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {
  setMySubmissionsSortBy,
  setMySubmissionsIsLoading,
  setMySubmissionsStats,
  removeBenchmarkUser,
  addBenchmarkUser,
  sendQuery,
  resultUserQuery,
  addBenchmarkUserSuccess,
  fetchRecentVisitors,
  fetchRecentVisitorsSuccess,
} from '../actions/statsSection';
import { API_BASE_PATH } from '../../constants';
import { getProfileOwnerUser } from '../selectors/users';
import { MySubmissionsSortBy } from '../../types/statsSection';
import { setCsrfOnParams } from './streams';

function* fetchFromApi(requestUrl, params) {
  params = yield setCsrfOnParams(params);
  return yield axios
    .get(`${API_BASE_PATH}/${requestUrl}`, { params })
    .catch(response => false);
}

function* updateBenchmarkUsers(data, type: 'add' | 'remove') {
  return yield call(request, {
    method: 'post',
    baseURL: API_BASE_PATH,
    url: `/stats/comparelist/${type}`,
    data,
  });
}

export function* removeBenchmarkUserSaga(
  action: ReturnType<typeof removeBenchmarkUser>
) {
  const userId = action.payload;
  yield call(
    updateBenchmarkUsers,
    {
      userid: userId,
    },
    'remove'
  );
}

export function* addBenchmarkUserSaga(
  action: ReturnType<typeof addBenchmarkUser>
) {
  const userId = action.payload;
  const result = yield call(
    updateBenchmarkUsers,
    {
      userid: userId,
    },
    'add'
  );

  if (result.success) {
    yield put(
      addBenchmarkUserSuccess({
        timePeriods: result.compareYourself.timePeriods,
      })
    );
  }
}

function* fetchFriendsForLetter(search) {
  const result = yield call(request, {
    method: 'get',
    url: `/user/friendsforletter`,
    params: {
      search,
      letter: search[0],
      page: 0,
    },
  });
  return result;
}

export function* setMySubmissionsSortBySaga(
  action: ReturnType<typeof setMySubmissionsSortBy>
): SagaIterator {
  const { payload: sortBy } = action;
  const profileOwnerUser = yield select(getProfileOwnerUser);

  yield put(setMySubmissionsIsLoading(true));

  const sortByToSnakeCaseMap = {
    [MySubmissionsSortBy.PublishDate]: 'publish_date',
    [MySubmissionsSortBy.PageViews]: 'page_views',
    [MySubmissionsSortBy.Faves]: 'faves',
    [MySubmissionsSortBy.Comments]: 'comments',
  };

  const params = {
    username: profileOwnerUser.username,
    sort_by: sortByToSnakeCaseMap[sortBy],
  };

  const result = yield call(fetchFromApi, '/stats/my_content', params);

  if (result && result.data && !isApiError(result.data)) {
    yield all([
      put(setMySubmissionsStats(result.data.myContent)),
      put(setMySubmissionsIsLoading(false)),
    ]);
  }
}

export function* fetchRecentVisitorsSaga(
  action: ReturnType<typeof fetchRecentVisitors>
) {
  const offset = action.payload;
  const result = yield call(fetchFromApi, '/module/visitors', { offset });
  if (result && result.data && !isApiError(result.data)) {
    yield put(fetchRecentVisitorsSuccess(result.data));
  }
}

export function* sendUserQuery(action: ReturnType<typeof sendQuery>) {
  const query = action.payload;

  if (query.length < 3) {
    yield put(resultUserQuery([]));
    return;
  }

  const result = yield call(fetchFriendsForLetter, query);

  if (result.error || !result.users || result.users.length === 0) {
    return;
  }

  const regexp = new RegExp(`^${query}`, 'i');
  const foundUsers = result.users.filter(user => user.username.match(regexp));

  yield put(
    resultUserQuery(
      // unique
      foundUsers.filter(
        (user, index) =>
          foundUsers.findIndex(u => u.userId === user.userId) === index
      )
    )
  );
}

export default function* statsSectionSaga() {
  yield all([
    takeEvery(setMySubmissionsSortBy, setMySubmissionsSortBySaga),
    takeEvery(removeBenchmarkUser, removeBenchmarkUserSaga),
    takeEvery(addBenchmarkUser, addBenchmarkUserSaga),
    takeEvery(fetchRecentVisitors, fetchRecentVisitorsSaga),
    takeLatest(sendQuery, sendUserQuery),
  ]);
}
